import { TransStatement } from './api/model';
import { MetadataRule, TransactionMetadata } from './api/models.pleased';
import { switcher } from './bot/flow-frameworks/switcher';
import { TransactionStatus, TransactionType } from './config';
import { formatDate, MINUTE } from './date';
import { formatMoney, replacePlaceholders } from './utils';
import { ConnectToAgent } from './bot/types';
import { SelectedTransaction } from '@/store/transaction/transaction.store';
import { TRANSACTION_STATUS_ENUM, TRANSACTION_STATUS_NAME_ENUM } from './transaction-utils';
import * as env from '@/common/environment';

export interface UserPlaceholderFields {
  name: string;
  email: string;
  phone: string;
  foreignId: string | number;
}

export enum RuleCondition {
  DEPOSIT_FAILED = 'DEPOSIT_FAILED',
  DEPOSIT_SUCCESSFUL = 'DEPOSIT_SUCCESSFUL',
  DEPOSIT_PENDING_TO_PENDING = 'DEPOSIT_PENDING_TO_PENDING',
  DEPOSIT_PENDING_TO_SUCCESSFUL = 'DEPOSIT_PENDING_TO_SUCCESSFUL',
  DEPOSIT_PENDING_TO_FAILED = 'DEPOSIT_PENDING_TO_FAILED',

  WITHDRAWAL_FAILED = 'WITHDRAWAL_FAILED',
  WITHDRAWAL_SUCCESSFUL = 'WITHDRAWAL_SUCCESSFUL',
  WITHDRAWAL_PENDING_TO_PENDING = 'WITHDRAWAL_PENDING_TO_PENDING',
  WITHDRAWAL_PENDING_TO_SUCCESSFUL = 'WITHDRAWAL_PENDING_TO_SUCCESSFUL',
  WITHDRAWAL_PENDING_TO_FAILED = 'WITHDRAWAL_PENDING_TO_FAILED'
}

export interface TransactionRefreshed extends SelectedTransaction {
  newStatus?: TransactionStatus;
}

const metadataRuleSwitcher = switcher<
  RuleCondition,
  { type: number; status: number[]; newStatus?: number[] }
>()
  .when(RuleCondition.DEPOSIT_FAILED, () => ({
    type: TransactionType.Deposit,
    status: TRANSACTION_STATUS_ENUM.FAILED,
  }))
  .when(RuleCondition.DEPOSIT_SUCCESSFUL, () => ({
    type: TransactionType.Deposit,
    status: TRANSACTION_STATUS_ENUM.SUCCESSFUL,
  }))
  .when(RuleCondition.DEPOSIT_PENDING_TO_PENDING, () => ({
    type: TransactionType.Deposit,
    status: TRANSACTION_STATUS_ENUM.PENDING,
    newStatus: TRANSACTION_STATUS_ENUM.PENDING,
  }))
  .when(RuleCondition.DEPOSIT_PENDING_TO_SUCCESSFUL, () => ({
    type: TransactionType.Deposit,
    status: TRANSACTION_STATUS_ENUM.PENDING,
    newStatus: TRANSACTION_STATUS_ENUM.SUCCESSFUL,
  }))
  .when(RuleCondition.DEPOSIT_PENDING_TO_FAILED, () => ({
    type: TransactionType.Deposit,
    status: TRANSACTION_STATUS_ENUM.PENDING,
    newStatus: TRANSACTION_STATUS_ENUM.FAILED,
  }))
  .when(RuleCondition.WITHDRAWAL_FAILED, () => ({
    type: TransactionType.Withdrawal,
    status: TRANSACTION_STATUS_ENUM.FAILED,
  }))
  .when(RuleCondition.WITHDRAWAL_SUCCESSFUL, () => ({
    type: TransactionType.Withdrawal,
    status: TRANSACTION_STATUS_ENUM.SUCCESSFUL,
  }))
  .when(RuleCondition.WITHDRAWAL_PENDING_TO_PENDING, () => ({
    type: TransactionType.Withdrawal,
    status: TRANSACTION_STATUS_ENUM.PENDING,
    newStatus: TRANSACTION_STATUS_ENUM.PENDING,
  }))
  .when(RuleCondition.WITHDRAWAL_PENDING_TO_SUCCESSFUL, () => ({
    type: TransactionType.Withdrawal,
    status: TRANSACTION_STATUS_ENUM.PENDING,
    newStatus: TRANSACTION_STATUS_ENUM.SUCCESSFUL,
  }))
  .when(RuleCondition.WITHDRAWAL_PENDING_TO_FAILED, () => ({
    type: TransactionType.Withdrawal,
    status: TRANSACTION_STATUS_ENUM.PENDING,
    newStatus: TRANSACTION_STATUS_ENUM.FAILED,
  }))
  .none(type => console.error('[TRANSACTION_TYPE]: Not identified', { type }));

function isConditionNotSpecified (condition: any) {
  return condition === undefined || condition === null;
}

function toBooleanOrNull (value: any) {
  const isNullish = value === undefined || value === null;
  return isNullish ? null : Boolean(value);
}

function checkMetadataRuleForTransaction ({
  transaction,
  transactionType,
  userGreyListed,
  transactionMetadata,
  rule,
}: {
  transaction: TransStatement & { newStatus?: TransactionStatus };
  transactionType: TransactionType;
  userGreyListed: boolean;
  transactionMetadata: TransactionMetadata;
  rule: MetadataRule;
}): boolean {
  // Check if the rule applies to the transaction
  const { type, status, newStatus } = metadataRuleSwitcher.match(
    rule.metadataAutomation as RuleCondition
  );

  const {
    timeSinceDirection,
    timeSinceTransaction,
    userGreyListed: userGreyListedCondition,
    paymentProviderIds,
    sessionIdExists,
    tradeNumberExists,
    transactionNumberExists,
    nameConfirmed,
  } = rule;

  const isConditionMet =
    type === transactionType &&
    status.includes(transaction.status) &&
    (!newStatus || (transaction.newStatus && newStatus.includes(transaction.newStatus)));

  const minutesPassed = (Date.now() - transaction.createTime) / MINUTE;

  const isTimeConditionMet =
    !timeSinceTransaction ||
    (timeSinceDirection === 'LESS_THAN'
      ? minutesPassed < timeSinceTransaction
      : minutesPassed > timeSinceTransaction);

  const isUserGreyListedConditionMet = isConditionNotSpecified(userGreyListedCondition) || userGreyListedCondition === userGreyListed;

  const isPaymentProviderConditionMet =
    !paymentProviderIds || !paymentProviderIds.length || paymentProviderIds.includes(transaction.payChId);

  const {
    sessionId,
    tradeNumber,
    transactionNumber,
    isNameConfirmed,
  } = transactionMetadata;

  const isSessionExistsConditionMet = isConditionNotSpecified(sessionIdExists) || sessionIdExists === toBooleanOrNull(sessionId);
  const isTradeNumberExistsConditionMet = isConditionNotSpecified(tradeNumberExists) || tradeNumberExists === toBooleanOrNull(tradeNumber);
  const isTransactionNumberExistsConditionMet = isConditionNotSpecified(transactionNumberExists) || transactionNumberExists === toBooleanOrNull(transactionNumber);
  const isNameConfirmedConditionMet = isConditionNotSpecified(nameConfirmed) || nameConfirmed === toBooleanOrNull(isNameConfirmed);

  return Boolean(
    isConditionMet &&
    isTimeConditionMet &&
    isUserGreyListedConditionMet &&
    isPaymentProviderConditionMet &&
    isSessionExistsConditionMet &&
    isTradeNumberExistsConditionMet &&
    isTransactionNumberExistsConditionMet &&
    isNameConfirmedConditionMet
  );
}

export function getMetMetadataRulesForTransaction ({
  transaction,
  transactionType,
  userGreyListed,
  transactionMetadata,
  rules,
}: {
  transaction: TransactionRefreshed;
  transactionType: TransactionType;
  userGreyListed: boolean;
  transactionMetadata: TransactionMetadata;
  rules: MetadataRule[];
}): MetadataRule[] {
  return rules.sort((a, b) => {
    return a.displayOrder - b.displayOrder;
  }).filter(rule =>
    checkMetadataRuleForTransaction({
      transaction,
      transactionType,
      userGreyListed,
      transactionMetadata,
      rule,
    })
  );
}

export function replaceMetadataResponseText ({
  text,
  transaction,
  userFields,
  transactionMetadata,
}: {
  text: string;
  transaction: TransStatement;
  userFields: UserPlaceholderFields;
  transactionMetadata: TransactionMetadata;
}) {
  const { userName: name } = env.get('default') || { userName: '' };

  return replacePlaceholders(text, {
    transaction: {
      ...transaction,
      status: TRANSACTION_STATUS_NAME_ENUM[transaction.status] || '',
      source: transaction.counterFull,
      time: formatDate(transaction.createTime, true),
      amount: formatMoney(transaction.amount),
      id: transactionMetadata.transactionNumber,
      sessionId: transactionMetadata.sessionId,
    },
    requester: userFields,
  }, {
    requester: {
      name,
    },
  });
}

export function checkMetadataRulesForTransaction ({
  transaction,
  transactionType,
  userGreyListed,
  userFields,
  connectConfig,
  transactionMetadata,
}: {
  transaction: TransactionRefreshed;
  transactionType: TransactionType;
  userGreyListed: boolean;
  userFields: UserPlaceholderFields;
  connectConfig: ConnectToAgent | null;
  transactionMetadata: TransactionMetadata;
}) {
  const metRules = getMetMetadataRulesForTransaction({
    transaction,
    transactionType,
    userGreyListed,
    transactionMetadata,
    rules: connectConfig?.metadataRules || [],
  });

  if (!metRules.length || !connectConfig) return null;

  const firstMetRule = metRules[0];

  const responseText = replaceMetadataResponseText({
    text: (firstMetRule.messages && firstMetRule.messages[0]) || '',
    transaction,
    userFields,
    transactionMetadata,
  });

  return { responseText, rule: firstMetRule };
};
